import { Checkbox, FormControlLabel } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { requisitesDistributionApi, shopsApi, tradersApi } from 'api';
import {
  DataGridColumnDefinition,
  CrudPage,
  CopyTextId,
  ExpandedJson,
  dataGridColumns,
} from 'components';
import { FilterDefinitionType, QueryKey } from 'enums';
import { usePartialQuery, useShopsQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { RequisitesDistributionDebug, FilterDefinition } from 'types';

export type RequisitesDistributionDebugFilters = {
  orderId: string;
  requisitesId: string;
  shopId: string;
  traderId: string;
};

type Props = {
  embedded?: boolean;
};

export const RequisitesDistributionDebugPage: React.FC<Props> = ({
  embedded = false,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.distribution_debug',
  });
  const { role } = useUser();
  const [showSuccessfulChecks, setShowSuccessfulChecks] = useState(false);

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );
  const queryResultTraders = useQuery(QueryKey.Traders, () =>
    tradersApi.getAllAsRole(role)(),
  );

  const queryResult = usePartialQuery(
    QueryKey.RequisitesDistributionDebug,
    requisitesDistributionApi.getDebugPaginated,
  );

  const columns =
    useMemo((): DataGridColumnDefinition<RequisitesDistributionDebug>[] => {
      const baseColumns: DataGridColumnDefinition<RequisitesDistributionDebug>[] =
        [
          dataGridColumns.getIdColumn(),
          {
            header: t('fields.order_id'),
            valueGetter: (item) => <CopyTextId id={item.orderId} />,
          },
          {
            header: t('fields.shop'),
            valueGetter: (item) => (
              <div>
                <div>{item.shop?.name}</div>
                <CopyTextId id={item.shopId} />
              </div>
            ),
          },
          {
            header: t('fields.trader'),
            valueGetter: (item) => <CopyTextId id={item.traderId} />,
          },
          {
            header: t('fields.requisites'),
            valueGetter: (item) => <CopyTextId id={item.requisitesId} />,
          },
          {
            header: t('fields.checks_summary'),
            valueGetter: (item) => (
              <div>
                <div className="tw-text-green-600">
                  {`✓ ${item.checkResults?.passed || 0}`}
                </div>
                <div className="tw-text-red-600">
                  {`✗ ${item.checkResults?.failed || 0}`}
                </div>
              </div>
            ),
          },
        ];

      if (showSuccessfulChecks) {
        baseColumns.push({
          header: t('fields.successful_checks'),
          valueGetter: (item) => (
            <>
              {item.checkResults?.success?.length > 0 ? (
                <ExpandedJson json={item.checkResults.success} maxLines={3} />
              ) : (
                <div className="tw-text-gray-500 tw-italic">
                  {t('messages.no_successful_checks')}
                </div>
              )}
            </>
          ),
          valueClassName: 'tw-w-[400px]',
        });
      }

      baseColumns.push({
        header: t('fields.failed_checks'),
        valueGetter: (item) => (
          <>
            {item.checkResults?.failures?.length > 0 ? (
              <ExpandedJson json={item.checkResults.failures} maxLines={3} />
            ) : (
              <div className="tw-text-green-600 tw-italic">
                {t('messages.all_checks_passed')}
              </div>
            )}
          </>
        ),
        valueClassName: 'tw-w-[400px]',
      });

      return baseColumns;
    }, [t, showSuccessfulChecks]);

  const filtersDefinitions: FilterDefinition<RequisitesDistributionDebugFilters>[] =
    useMemo(
      () => [
        {
          label: t('filters.order_id'),
          name: 'orderId',
          type: FilterDefinitionType.Text,
          format: 'uuid',
        },
        {
          label: t('filters.shop'),
          name: 'shopId',
          type: FilterDefinitionType.Shop,
          shops: queryResultShops.data,
        },
        {
          label: t('filters.trader'),
          name: 'traderId',
          type: FilterDefinitionType.Trader,
          traders: queryResultTraders.data,
        },
        {
          label: t('filters.requisites_id'),
          name: 'requisitesId',
          type: FilterDefinitionType.Text,
          format: 'uuid',
        },
      ],
      [queryResultShops.data, queryResultTraders.data, t],
    );

  return (
    <CrudPage
      header={
        embedded
          ? { hide: true }
          : {
              title: t('title'),
              subcontent: (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showSuccessfulChecks}
                      onChange={(e) =>
                        setShowSuccessfulChecks(e.target.checked)
                      }
                    />
                  }
                  label={t('controls.show_successful_checks')}
                />
              ),
            }
      }
      filters={{ filtersDefinitions }}
      afterFiltersContent={
        embedded ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={showSuccessfulChecks}
                onChange={(e) => setShowSuccessfulChecks(e.target.checked)}
              />
            }
            label={t('controls.show_successful_checks')}
          />
        ) : undefined
      }
      table={{ queryResult, columns, paginated: true }}
    />
  );
};
